/**
 * Centralized API Client
 * Provides consistent HTTP request handling with authentication and error management
 */

import { apiConfig, apiErrorMessages } from '../config/app.config';
import {
  ApiResponse,
  ApiError,
  ApiErrorCode,
  RequestOptions,
  QueryParams,
  HttpMethod,
  statusToErrorCode,
} from '../dtos/common/api.dto';
import { TokenService } from './auth/token.service';
import { refreshSession } from './auth/cognito.service';
import { ACCOUNT_STATE_CHANGED_EVENT } from '../constants/navigationEvents';
import { deviceHeaders } from './offline/deviceContext';

/** Phase 16C (design §6.6): listeners for the server's Date header (offline time anchor). */
type ServerTimeListener = (serverAt: string, deviceAt: string) => void;
const serverTimeListeners = new Set<ServerTimeListener>();

/** Called with the server time on every response that exposes a Date header. Returns an unsubscribe. */
export function onServerTime(listener: ServerTimeListener): () => void {
  serverTimeListeners.add(listener);
  return () => { serverTimeListeners.delete(listener); };
}

function reportServerTime(response: Response): void {
  const raw = typeof response.headers?.get === 'function' ? response.headers.get('Date') : null;
  const parsed = raw ? Date.parse(raw) : NaN;
  if (!Number.isFinite(parsed)) return;
  const serverAt = new Date(parsed).toISOString();
  const deviceAt = new Date().toISOString();
  serverTimeListeners.forEach((l) => {
    try { l(serverAt, deviceAt); } catch { /* a listener never breaks a request */ }
  });
}

/** 402 codes that mean the tenant's account state changed (Phase 13). */
const ACCOUNT_STATE_CODES: ReadonlySet<string> = new Set(['ACCOUNT_READ_ONLY', 'ACCOUNT_SUSPENDED', 'ACCOUNT_CLOSED']);

/**
 * Default request options
 */
const DEFAULT_OPTIONS: RequestOptions = {
  timeout: apiConfig.timeout,
  retries: 0,
};

/**
 * Build URL with query parameters
 */
function buildUrl(endpoint: string, params?: QueryParams): string {
  const baseUrl = endpoint.startsWith('http') ? endpoint : `${apiConfig.baseUrl}${endpoint}`;

  if (!params) {
    return baseUrl;
  }

  const url = new URL(baseUrl);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (typeof value === 'object') {
        url.searchParams.append(key, JSON.stringify(value));
      } else {
        url.searchParams.append(key, String(value));
      }
    }
  });

  return url.toString();
}

/**
 * Get headers for request
 */
function getHeaders(options?: RequestOptions): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  // Add authorization header unless explicitly skipped
  if (!options?.skipAuth) {
    const authHeader = TokenService.getAuthorizationHeader();
    if (authHeader) {
      headers['Authorization'] = authHeader;
    }
    // Phase 16C: X-Device-Class on phones and tablets, X-Device-Id on an unlocked enrolled device
    Object.assign(headers, deviceHeaders());
  }

  if (options?.idempotencyKey) {
    headers['Idempotency-Key'] = options.idempotencyKey;
  }

  // Merge custom headers
  if (options?.headers) {
    Object.assign(headers, options.headers);
  }

  return headers;
}

/**
 * Parse error response, preserving structured error.code/details when the backend provides them
 */
async function parseErrorResponse(
  response: Response
): Promise<{ message: string; details?: Record<string, unknown>; domainCode?: string }> {
  try {
    const data = await response.json();
    const message = data.message || data.error?.message || data.error || data.detail || response.statusText;
    const errorDetails = data.error?.details && typeof data.error.details === 'object' ? data.error.details : undefined;
    // generateApiResponse(status, message, data, error) also carries the domain code and any extra
    // payload (e.g. possibleDuplicates on 409 POSSIBLE_DUPLICATE) in `data` (Phase 6 §8).
    const bodyData =
      data.data && typeof data.data === 'object' && !Array.isArray(data.data)
        ? (data.data as Record<string, unknown>)
        : undefined;
    const details = errorDetails || bodyData ? { ...(bodyData || {}), ...(errorDetails || {}) } : undefined;
    const rawCode = typeof bodyData?.code === 'string' ? bodyData.code : data.error?.code;
    const domainCode = typeof rawCode === 'string' && rawCode.length > 0 ? rawCode : undefined;
    return { message, details, domainCode };
  } catch {
    return { message: response.statusText || 'Unknown error' };
  }
}

/**
 * Handle API response
 */
async function handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
  if (!response.ok) {
    const { message, details, domainCode } = await parseErrorResponse(response);
    const errorCode = statusToErrorCode(response.status);
    if (response.status === 402 && domainCode && ACCOUNT_STATE_CODES.has(domainCode) && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(ACCOUNT_STATE_CHANGED_EVENT, { detail: { code: domainCode } }));
    }

    throw new ApiError(message, errorCode, response.status, details, domainCode);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {
      success: true,
      data: undefined as unknown as T,
    };
  }

  try {
    const data = await response.json();

    // Handle different response formats from backend
    // Some endpoints return { data: T }, others return { message: T } (object), others return T directly
    let responseData: unknown;
    if (data.data !== undefined) {
      responseData = data.data;
    } else if (data.message !== undefined && typeof data.message === 'object' && data.message !== null) {
      responseData = data.message;
    } else {
      responseData = data;
    }

    return {
      success: true,
      data: responseData as T,
      pagination: data.pagination,
      message: typeof data.message === 'string' ? data.message : undefined,
    };
  } catch {
    // If JSON parsing fails but response was ok, return empty success
    return {
      success: true,
      data: undefined as unknown as T,
    };
  }
}

/**
 * Execute fetch request with timeout
 */
async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeout: number
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...init,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * One shared refresh for concurrent 401s (SEC-09, Phase 2).
 */
let refreshInFlight: Promise<boolean> | null = null;

function refreshOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = refreshSession().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * Send a request; on 401 refresh the tokens once and retry once.
 * If the refresh fails or the retry is still 401, clear the tokens (sign-out).
 * `build` is called per attempt so the retry carries the new Authorization header.
 */
async function sendWithRefresh(
  url: string,
  build: () => RequestInit,
  timeout: number,
  skipAuth?: boolean
): Promise<Response> {
  const response = await fetchWithTimeout(url, build(), timeout);
  if (response.status !== 401 || skipAuth) {
    return response;
  }
  const refreshed = await refreshOnce();
  if (!refreshed) {
    TokenService.clearTokens();
    return response;
  }
  const retry = await fetchWithTimeout(url, build(), timeout);
  if (retry.status === 401) {
    TokenService.clearTokens();
  }
  return retry;
}

/**
 * GET requests in flight, by URL (and whether they carry the JWT). A component that asks for data
 * another component is already loading shares that request instead of sending its own (e.g. a page
 * load where the dashboard, banners and Organization Account all want /users/me/permissions).
 * Only concurrent calls share: nothing is cached once the response arrives. Any write clears the
 * map, so a read issued after a change never joins a request that started before it.
 */
const inFlightGets = new Map<string, Promise<ApiResponse<unknown>>>();

/** Each caller gets its own copy, so one component changing its response cannot affect another. */
function copyResponse<T>(response: ApiResponse<T>): ApiResponse<T> {
  if (typeof structuredClone === 'function') return structuredClone(response);
  return JSON.parse(JSON.stringify(response)) as ApiResponse<T>;
}

/**
 * API Client class
 */
class ApiClientClass {
  /**
   * Make a request
   */
  private async request<T>(
    method: HttpMethod,
    endpoint: string,
    data?: unknown,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
    const url = buildUrl(endpoint, method === 'GET' ? (data as QueryParams) : undefined);
    if (method !== 'GET') inFlightGets.clear();

    const build = (): RequestInit => {
      const init: RequestInit = {
        method,
        headers: getHeaders(mergedOptions),
      };

      // Add body for non-GET requests
      if (method !== 'GET' && data !== undefined) {
        init.body = JSON.stringify(data);
      }

      // Use external signal if provided
      if (mergedOptions.signal) {
        init.signal = mergedOptions.signal;
      }
      return init;
    };

    try {
      const response = await sendWithRefresh(
        url,
        build,
        mergedOptions.timeout || apiConfig.timeout,
        mergedOptions.skipAuth
      );
      reportServerTime(response);

      return await handleResponse<T>(response);
    } catch (error) {
      // Handle abort/timeout
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new ApiError(apiErrorMessages.timeout, ApiErrorCode.TIMEOUT, 408);
      }

      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiError(apiErrorMessages.network, ApiErrorCode.NETWORK_ERROR, 0);
      }

      // Re-throw ApiError
      if (error instanceof ApiError) {
        throw error;
      }

      // Wrap unknown errors
      throw new ApiError(
        error instanceof Error ? error.message : apiErrorMessages.default,
        ApiErrorCode.UNKNOWN,
        500
      );
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, params?: QueryParams, options?: RequestOptions): Promise<ApiResponse<T>> {
    // A caller with its own abort signal keeps its own request (aborting must not cancel the others)
    if (options?.signal) return this.request<T>('GET', endpoint, params, options);
    const key = `${options?.skipAuth ? 'public' : 'auth'} ${buildUrl(endpoint, params)}`;
    let shared = inFlightGets.get(key) as Promise<ApiResponse<T>> | undefined;
    if (!shared) {
      shared = this.request<T>('GET', endpoint, params, options);
      inFlightGets.set(key, shared as Promise<ApiResponse<unknown>>);
      const forget = () => { if (inFlightGets.get(key) === shared) inFlightGets.delete(key); };
      shared.then(forget, forget);
    }
    return copyResponse(await shared);
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('POST', endpoint, data, options);
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', endpoint, data, options);
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', endpoint, data, options);
  }

  /**
   * DELETE request
   * @param endpoint - API endpoint
   * @param data - Optional request body (useful for DELETE with data)
   * @param options - Request options
   */
  async delete<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', endpoint, data, options);
  }

  /**
   * Upload file(s)
   */
  async upload<T>(
    endpoint: string,
    formData: FormData,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    const url = buildUrl(endpoint);
    inFlightGets.clear();

    const build = (): RequestInit => {
      const headers: Record<string, string> = {};

      // Don't set Content-Type for FormData - browser will set it with boundary
      if (!options?.skipAuth) {
        const authHeader = TokenService.getAuthorizationHeader();
        if (authHeader) {
          headers['Authorization'] = authHeader;
        }
      }

      if (options?.headers) {
        Object.assign(headers, options.headers);
      }
      return {
        method: 'POST',
        headers,
        body: formData,
        signal: options?.signal,
      };
    };

    try {
      const response = await sendWithRefresh(
        url,
        build,
        options?.timeout || apiConfig.timeout,
        options?.skipAuth
      );

      return await handleResponse<T>(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        error instanceof Error ? error.message : apiErrorMessages.default,
        ApiErrorCode.UNKNOWN,
        500
      );
    }
  }

  /**
   * GET a non-JSON body (e.g. a CSV export) as a Blob, through the same auth header and
   * 401 refresh-and-retry as JSON requests. Errors throw ApiError like the other methods.
   */
  async download(endpoint: string, params?: QueryParams, options: RequestOptions = {}): Promise<Blob> {
    const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
    const url = buildUrl(endpoint, params);
    const build = (): RequestInit => ({
      method: 'GET',
      headers: getHeaders(mergedOptions),
      signal: mergedOptions.signal,
    });
    try {
      const response = await sendWithRefresh(url, build, mergedOptions.timeout || apiConfig.timeout, mergedOptions.skipAuth);
      if (!response.ok) {
        const { message, details, domainCode } = await parseErrorResponse(response);
        throw new ApiError(message, statusToErrorCode(response.status), response.status, details, domainCode);
      }
      return await response.blob();
    } catch (error) {
      if (error instanceof ApiError) throw error;
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new ApiError(apiErrorMessages.timeout, ApiErrorCode.TIMEOUT, 408);
      }
      throw new ApiError(error instanceof Error ? error.message : apiErrorMessages.default, ApiErrorCode.UNKNOWN, 500);
    }
  }

  /**
   * Upload a file straight to S3 with a pre-signed POST (Phase 8, Q2).
   * Not an API Gateway call: no Authorization header is sent (S3 rejects it) and the signed
   * `fields` go first, the file last, as S3 requires. Uses XMLHttpRequest so the caller gets
   * real progress from `upload.onprogress` (fetch has no upload progress).
   */
  uploadToPresignedPost(
    url: string,
    fields: Record<string, string>,
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.timeout = Math.max(apiConfig.timeout, 120000);

      if (onProgress) {
        xhr.upload.onprogress = (event: ProgressEvent) => {
          if (event.lengthComputable && event.total > 0) {
            onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(100);
          resolve();
          return;
        }
        // S3 answers with XML; its text is not shown to users (may name the bucket/key).
        const message =
          xhr.status === 400 || xhr.status === 403
            ? 'The file was rejected by storage (too large, wrong type, or the upload link expired). Try again.'
            : 'The file upload failed. Try again.';
        reject(new ApiError(message, statusToErrorCode(xhr.status), xhr.status));
      };
      xhr.onerror = () => reject(new ApiError(apiErrorMessages.network, ApiErrorCode.NETWORK_ERROR, 0));
      xhr.ontimeout = () => reject(new ApiError(apiErrorMessages.timeout, ApiErrorCode.TIMEOUT, 408));
      xhr.onabort = () => reject(new ApiError('The file upload was cancelled.', ApiErrorCode.UNKNOWN, 0));

      xhr.send(formData);
    });
  }
}

// Export singleton instance
export const apiClient = new ApiClientClass();

// Export class for testing
export { ApiClientClass };

// Export utility functions
export { buildUrl, getHeaders, sendWithRefresh };

export default apiClient;
